import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Alert,
  Box,
  Card,
  CardContent,
  Grid,
  LinearProgress,
  MenuItem,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";
import {
  CartesianGrid,
  Legend,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import { getCombinedTransactionsReport } from "./reportApi";
import { formatPersonName } from "../../../utils/formatPersonName";

const REGIMES = {
  mei: "MEI",
  simples: "Simples Nacional",
  pf: "Pessoa Física (Carnê-Leão)",
};

const MEI_LIMIT = 81000;
const MEI_DAS_FIXED = 76;

const BRACKETS_SIMPLES_III = [
  { max: 180000, rate: 0.06 },
  { max: 360000, rate: 0.112 },
  { max: 720000, rate: 0.135 },
  { max: 1800000, rate: 0.16 },
  { max: 3600000, rate: 0.21 },
  { max: Infinity, rate: 0.33 },
];

const BRACKETS_SIMPLES_V = [
  { max: 180000, rate: 0.155 },
  { max: 360000, rate: 0.18 },
  { max: 720000, rate: 0.195 },
  { max: 1800000, rate: 0.205 },
  { max: 3600000, rate: 0.23 },
  { max: Infinity, rate: 0.305 },
];

const IRPF_PROGRESSIVE_MONTHLY = [
  { max: 2259.2, rate: 0, deduction: 0 },
  { max: 2826.65, rate: 0.075, deduction: 169.44 },
  { max: 3751.05, rate: 0.15, deduction: 381.44 },
  { max: 4664.68, rate: 0.225, deduction: 662.77 },
  { max: Infinity, rate: 0.275, deduction: 896.0 },
];

const DEDUCTIBLE_CATEGORIES = new Set([
  "infraestrutura",
  "educacao",
  "marketing",
  "aluguel",
  "contabilidade",
  "servico",
]);

const OPERATIONAL_CATEGORIES = new Set([
  "infraestrutura",
  "educacao",
  "marketing",
  "operacional",
  "software",
  "servico",
  "aluguel",
]);

const normalize = (value) =>
  String(value || "")
    .normalize("NFKD")
    .replace(/[^\w\s-]/g, "")
    .toLowerCase()
    .trim();

const toMoney = (value) =>
  new Intl.NumberFormat("pt-BR", { style: "currency", currency: "BRL" }).format(
    Number(value || 0)
  );

const getAliquotaByBracket = (brackets, value) =>
  brackets.find((item) => value <= item.max)?.rate || 0;

const getIrpfMonthlyTax = (baseMensal) => {
  const faixa = IRPF_PROGRESSIVE_MONTHLY.find((item) => baseMensal <= item.max);
  if (!faixa) return 0;
  return Math.max(baseMensal * faixa.rate - faixa.deduction, 0);
};

const IncomeTax = () => {
  const currentYear = new Date().getFullYear();
  const [selectedYear, setSelectedYear] = useState(currentYear);
  const [regime, setRegime] = useState("simples");
  const [folha12m, setFolha12m] = useState(0);
  const [transactions, setTransactions] = useState([]);

  const fetchData = useCallback(async () => {
    try {
      const data = await getCombinedTransactionsReport({
        start_date: `${selectedYear - 1}-01-01`,
        end_date: `${selectedYear}-12-31`,
      });
      setTransactions(data || []);
    } catch (error) {
      console.error("Erro ao carregar dados fiscais:", error);
      alert("Erro ao carregar dados fiscais.");
    }
  }, [selectedYear]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const { anoAtualTxs, trailing12, summary, chartData } = useMemo(() => {
    const yearTxs = transactions.filter(
      (item) => new Date(item.created_at).getFullYear() === selectedYear
    );

    const entriesYear = yearTxs.filter((item) => item.type === "entry");
    const expensesYear = yearTxs.filter((item) => item.type === "expense");
    const totalRecebido = entriesYear.reduce((sum, item) => sum + Number(item.amount || 0), 0);
    const totalDespesas = expensesYear.reduce((sum, item) => sum + Number(item.amount || 0), 0);

    const now = new Date();
    const trailingStart = new Date(now.getFullYear(), now.getMonth() - 11, 1);
    const trailingEnd = new Date(now.getFullYear(), now.getMonth() + 1, 0, 23, 59, 59);
    const trailingEntries = transactions.filter((item) => {
      if (item.type !== "entry") return false;
      const date = new Date(item.created_at);
      return date >= trailingStart && date <= trailingEnd;
    });
    const faturamento12m = trailingEntries.reduce(
      (sum, item) => sum + Number(item.amount || 0),
      0
    );

    const despesasComFlags = expensesYear.map((item) => {
      const category = normalize(item.category);
      return {
        ...item,
        isDeductible: DEDUCTIBLE_CATEGORIES.has(category),
        isOperational: OPERATIONAL_CATEGORIES.has(category),
      };
    });

    const totalDedutivel = despesasComFlags
      .filter((item) => item.isDeductible)
      .reduce((sum, item) => sum + Number(item.amount || 0), 0);

    const fatorR = faturamento12m > 0 ? Number(folha12m || 0) / faturamento12m : 0;
    const anexo = fatorR >= 0.28 ? "Anexo III" : "Anexo V";

    let baseCalculo = 0;
    let aliquota = 0;
    let impostoEstimado = 0;
    let regraResumo = "";

    if (regime === "mei") {
      const mesesComReceita = new Set(
        entriesYear.map((item) => new Date(item.created_at).toISOString().slice(0, 7))
      ).size;
      baseCalculo = totalRecebido;
      aliquota = 0;
      impostoEstimado = mesesComReceita * MEI_DAS_FIXED;
      regraResumo = `DAS fixo estimado: ${mesesComReceita} mês(es) x ${toMoney(MEI_DAS_FIXED)}`;
    } else if (regime === "simples") {
      baseCalculo = totalRecebido;
      const table = anexo === "Anexo III" ? BRACKETS_SIMPLES_III : BRACKETS_SIMPLES_V;
      aliquota = getAliquotaByBracket(table, faturamento12m);
      impostoEstimado = baseCalculo * aliquota;
      regraResumo = `Base no faturamento bruto. Faixa ${anexo} aplicada sobre os últimos 12 meses.`;
    } else {
      baseCalculo = Math.max(totalRecebido - totalDedutivel, 0);
      const baseMensal = baseCalculo / 12;
      const mensal = getIrpfMonthlyTax(baseMensal);
      aliquota = baseCalculo > 0 ? mensal / baseMensal : 0;
      impostoEstimado = mensal * 12;
      regraResumo =
        "Base no lucro líquido (receitas - despesas dedutíveis) com tabela progressiva mensal.";
    }

    const lucroDisponivel = totalRecebido - totalDespesas - impostoEstimado;

    const grouped = {};
    yearTxs.forEach((item) => {
      const month = new Date(item.created_at).toISOString().slice(0, 7);
      if (!grouped[month]) grouped[month] = { month, entradas: 0, saidas: 0 };
      if (item.type === "entry") grouped[month].entradas += Number(item.amount || 0);
      if (item.type === "expense") grouped[month].saidas += Number(item.amount || 0);
    });

    const evolution = Object.values(grouped)
      .map((item) => ({ ...item, lucro: item.entradas - item.saidas }))
      .sort((a, b) => a.month.localeCompare(b.month));

    return {
      anoAtualTxs: yearTxs,
      trailing12: { faturamento12m, fatorR, anexo },
      summary: {
        totalRecebido,
        totalDespesas,
        totalDedutivel,
        baseCalculo,
        aliquota,
        impostoEstimado,
        lucroDisponivel,
        regraResumo,
      },
      chartData: evolution,
    };
  }, [transactions, selectedYear, regime, folha12m]);

  const tetoMeiPercent = Math.min((summary.totalRecebido / MEI_LIMIT) * 100, 100);

  return (
    <Box sx={{ p: { xs: 1, sm: 2 } }}>
      <Typography variant="h5" sx={{ fontWeight: 700, mb: 1 }}>
        Fiscal (Estimativa)
      </Typography>

      <Alert severity="info" sx={{ mb: 2 }}>
        Modelo operacional aproximado para apoio de gestão. Validação fiscal oficial deve
        ser feita pelo contador.
      </Alert>

      <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1.2, mb: 2 }}>
        <TextField
          select
          size="small"
          label="Ano"
          value={selectedYear}
          onChange={(e) => setSelectedYear(Number(e.target.value))}
          sx={{ minWidth: 130 }}
        >
          {[...Array(6)].map((_, index) => {
            const year = currentYear - index;
            return (
              <MenuItem key={year} value={year}>
                {year}
              </MenuItem>
            );
          })}
        </TextField>

        <TextField
          select
          size="small"
          label="Regime"
          value={regime}
          onChange={(e) => setRegime(e.target.value)}
          sx={{ minWidth: 250 }}
        >
          {Object.entries(REGIMES).map(([key, label]) => (
            <MenuItem key={key} value={key}>
              {label}
            </MenuItem>
          ))}
        </TextField>

        {regime === "simples" && (
          <TextField
            size="small"
            label="Folha/Pró-labore 12m"
            type="number"
            value={folha12m}
            onChange={(e) => setFolha12m(Number(e.target.value) || 0)}
            sx={{ minWidth: 210 }}
          />
        )}
      </Box>

      <Grid container spacing={1.5} sx={{ mb: 2 }}>
        <Grid item xs={12} sm={6} md={3}>
          <Card><CardContent><Typography variant="body2">Receita bruta</Typography><Typography variant="h6" sx={{ fontWeight: 800, color: "success.main" }}>{toMoney(summary.totalRecebido)}</Typography></CardContent></Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card><CardContent><Typography variant="body2">Despesas operacionais</Typography><Typography variant="h6" sx={{ fontWeight: 800, color: "warning.main" }}>{toMoney(summary.totalDespesas)}</Typography></CardContent></Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card><CardContent><Typography variant="body2">Base de cálculo</Typography><Typography variant="h6" sx={{ fontWeight: 800 }}>{toMoney(summary.baseCalculo)}</Typography></CardContent></Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card><CardContent><Typography variant="body2">Imposto estimado</Typography><Typography variant="h6" sx={{ fontWeight: 800, color: "error.main" }}>{toMoney(summary.impostoEstimado)}</Typography></CardContent></Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card><CardContent><Typography variant="body2">Alíquota efetiva</Typography><Typography variant="h6" sx={{ fontWeight: 800 }}>{(summary.aliquota * 100).toFixed(2)}%</Typography></CardContent></Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card><CardContent><Typography variant="body2">Lucro disponível</Typography><Typography variant="h6" sx={{ fontWeight: 800, color: summary.lucroDisponivel >= 0 ? "success.main" : "error.main" }}>{toMoney(summary.lucroDisponivel)}</Typography></CardContent></Card>
        </Grid>
        {regime === "simples" && (
          <>
            <Grid item xs={12} sm={6} md={3}>
              <Card><CardContent><Typography variant="body2">Faturamento 12 meses</Typography><Typography variant="h6" sx={{ fontWeight: 800 }}>{toMoney(trailing12.faturamento12m)}</Typography></CardContent></Card>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <Card><CardContent><Typography variant="body2">Fator R / Anexo</Typography><Typography variant="h6" sx={{ fontWeight: 800 }}>{(trailing12.fatorR * 100).toFixed(2)}% • {trailing12.anexo}</Typography></CardContent></Card>
            </Grid>
          </>
        )}
      </Grid>

      {regime === "mei" && (
        <Paper sx={{ p: 1.5, mb: 2 }}>
          <Typography sx={{ fontWeight: 700, mb: 0.6 }}>Teto anual MEI</Typography>
          <Typography variant="body2" sx={{ mb: 0.7 }}>
            {toMoney(summary.totalRecebido)} de {toMoney(MEI_LIMIT)} ({tetoMeiPercent.toFixed(1)}%)
          </Typography>
          <LinearProgress
            variant="determinate"
            value={tetoMeiPercent}
            color={tetoMeiPercent >= 90 ? "warning" : "primary"}
            sx={{ height: 10, borderRadius: 999 }}
          />
        </Paper>
      )}

      <Paper sx={{ p: 1.5, mb: 2 }}>
        <Typography sx={{ fontWeight: 700, mb: 0.6 }}>Regra aplicada</Typography>
        <Typography variant="body2">{summary.regraResumo}</Typography>
      </Paper>

      <Paper sx={{ p: 1.5, mb: 2 }}>
        <Typography sx={{ fontWeight: 700, mb: 1 }}>Evolução mensal</Typography>
        <ResponsiveContainer width="100%" height={260}>
          <LineChart data={chartData}>
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis dataKey="month" />
            <YAxis />
            <Tooltip formatter={(value) => toMoney(value)} />
            <Legend />
            <Line type="monotone" dataKey="entradas" stroke="#2e7d32" name="Receitas" />
            <Line type="monotone" dataKey="saidas" stroke="#d32f2f" name="Despesas" />
            <Line type="monotone" dataKey="lucro" stroke="#1976d2" name="Lucro operacional" />
          </LineChart>
        </ResponsiveContainer>
      </Paper>

      <Paper sx={{ overflow: "hidden" }}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Data</TableCell>
              <TableCell>Tipo</TableCell>
              <TableCell>Descrição</TableCell>
              <TableCell>Categoria</TableCell>
              <TableCell>Flags</TableCell>
              <TableCell align="right">Valor</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {anoAtualTxs.map((item, index) => {
              const category = normalize(item.category);
              const isDeductible = item.type === "expense" && DEDUCTIBLE_CATEGORIES.has(category);
              const isOperational = item.type === "expense" && OPERATIONAL_CATEGORIES.has(category);

              return (
                <TableRow key={`${item.transaction_id || "tx"}-${index}`}>
                  <TableCell>{new Date(item.created_at).toLocaleDateString("pt-BR")}</TableCell>
                  <TableCell>{item.type === "entry" ? "Entrada" : "Saída"}</TableCell>
                  <TableCell>{formatPersonName(item.customer_name) || item.description || "-"}</TableCell>
                  <TableCell>{item.category || "-"}</TableCell>
                  <TableCell>
                    {item.type === "entry"
                      ? "-"
                      : `${isDeductible ? "Dedutível" : "Não dedutível"} • ${
                          isOperational ? "Operacional" : "Não operacional"
                        }`}
                  </TableCell>
                  <TableCell
                    align="right"
                    sx={{
                      fontWeight: 700,
                      color: item.type === "entry" ? "success.main" : "error.main",
                    }}
                  >
                    {toMoney(item.amount)}
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </Paper>
    </Box>
  );
};

export default IncomeTax;
